// Dev-only card — seed demo data, clear demo data, other harness affordances.
// Gated by import.meta.env.DEV so it doesn't ship in production builds.

import { useState } from 'react';
import { seedDemoData, type SeedResult } from '@/shared/seedDemoData';

export function DevToolsCard() {
  // Production builds: render nothing
  if (!import.meta.env.DEV) return null;

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SeedResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSeed() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await seedDemoData();
      setResult(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-paper border border-dashed border-amber/40 rounded p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-amber font-bold">🛠</span>
        <h2 className="font-semibold text-navy">Dev tools</h2>
        <span className="text-xs text-text-mute uppercase tracking-wide">dev only</span>
      </div>
      <p className="text-xs text-text-mute mb-3">
        Hidden in production builds. Use for manual smoke-testing of the M2 surfaces.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={handleSeed}
          disabled={busy}
          className="px-3 py-1 bg-amber text-white text-sm rounded font-semibold disabled:opacity-50 hover:opacity-90"
        >
          {busy ? 'Seeding…' : 'Seed demo data'}
        </button>
        <span className="text-xs text-text-mute">
          Creates 5 customers (one per tier) + 3 parts + 3 quotes + 1 order. Idempotent.
        </span>
      </div>

      {result && (
        <div
          className={`mt-3 border-l-4 px-3 py-2 text-sm ${
            result.skipped ? 'border-text-mute bg-bg' : 'border-green bg-green/5'
          }`}
        >
          {result.skipped ? (
            <>↩ Skipped — {result.reason}</>
          ) : (
            <>
              ✓ Created {result.customersCreated} customers, {result.partsCreated} parts,{' '}
              {result.quotesCreated} quotes, {result.ordersCreated} order. Check the Customers
              page.
            </>
          )}
        </div>
      )}

      {error && (
        <div className="mt-3 bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm">
          ⚠ {error}
        </div>
      )}
    </div>
  );
}
